import { createBrowserClient } from '@supabase/ssr'
import type { SupabaseClient } from '@supabase/supabase-js'

// No-op lock: bypass gotrue-js's navigator.locks-based serialization.
// Default lock occasionally orphans across tab reloads and deadlocks
// signInWithPassword / getSession indefinitely. Tradeoff: theoretical
// double-refresh race in concurrent tabs (harmless in practice).
const noopLock = async <T>(_name: string, _acquireTimeout: number, fn: () => Promise<T>): Promise<T> => fn()

// Singleton — instantiating createBrowserClient multiple times still
// creates competing locks even with no-op lock fallback in some paths.
let _client: SupabaseClient | undefined

export function createClient(): SupabaseClient {
  // Env-var validation deferred to call site (not module load) so Next.js's
  // "Collecting page data" build phase doesn't crash when env is sparse.
  const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL
  if (!supabaseUrl) throw new Error('NEXT_PUBLIC_SUPABASE_URL is required')
  const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY
  if (!supabaseAnonKey) throw new Error('NEXT_PUBLIC_SUPABASE_ANON_KEY is required')

  if (typeof window === 'undefined') {
    // SSR: each request gets its own client (no shared state)
    return createBrowserClient(supabaseUrl, supabaseAnonKey, {
      auth: { lock: noopLock },
    })
  }
  if (!_client) {
    _client = createBrowserClient(supabaseUrl, supabaseAnonKey, {
      auth: { lock: noopLock },
    })
  }
  return _client
}
